/**
 * 共通スライダー（4枚以上で Swiper・無限ループ、3枚以下はグリッド）
 * TOP 採用・メンバー詳細など `[data-js-c-slider]` を複数配置可
 * Webpack 4 は package exports を解決しないため、実ファイルパスで import する。
 */
import Swiper from 'swiper/swiper.mjs';
import Navigation from 'swiper/modules/navigation.mjs';
import Pagination from 'swiper/modules/pagination.mjs';
// CSS は webpack エントリ `swiper.css`（node_modules の swiper / pagination CSS）→ assets/css/swiper.css（wp_enqueue 済み）

const MIN_SLIDES_FOR_SLIDER = 4;

export default class Slider {
  init() {
    document.querySelectorAll('[data-js-c-slider]').forEach((viewport) => {
      this.initViewport(viewport);
    });
  }

  /**
   * 各カードを最大高さに揃える（4枚目以降も含めて全件計測）
   * リンクなしカード（`.c-slider__slide > .c-member-card`）も対象にする
   * @param {HTMLElement} viewport
   */
  equalizeCardHeights(viewport) {
    const cards = Array.from(
      viewport.querySelectorAll('.c-slider__slide > a, .c-slider__slide > .c-member-card'),
    );
    if (!cards.length) {
      return;
    }

    cards.forEach((card) => {
      card.style.minHeight = '';
    });

    let maxHeight = 0;
    cards.forEach((card) => {
      const height = card.getBoundingClientRect().height;
      if (height > maxHeight) {
        maxHeight = height;
      }
    });

    if (maxHeight <= 0) {
      return;
    }

    const fixedHeight = `${Math.ceil(maxHeight)}px`;
    cards.forEach((card) => {
      card.style.minHeight = fixedHeight;
    });
  }

  /**
   * @param {HTMLElement} viewport
   */
  initViewport(viewport) {
    const el = viewport.querySelector('.c-slider__swiper');
    if (!el) {
      return;
    }

    const prevEl = viewport.querySelector('[data-js-c-slider-prev]');
    const nextEl = viewport.querySelector('[data-js-c-slider-next]');
    const paginationEl = el.querySelector('.c-slider__pagination');
    if (!prevEl || !nextEl) {
      return;
    }

    const slideCount = el.querySelectorAll('.swiper-slide').length;
    if (slideCount < MIN_SLIDES_FOR_SLIDER) {
      viewport.classList.add('c-slider__viewport--static');
      viewport.classList.add(`c-slider__viewport--static-count-${slideCount}`);
      prevEl.hidden = true;
      nextEl.hidden = true;
      if (paginationEl) {
        paginationEl.hidden = true;
      }

      this.equalizeCardHeights(viewport);
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const swiperParams = {
      modules: paginationEl ? [Navigation, Pagination] : [Navigation],
      loop: true,
      watchOverflow: false,
      autoHeight: false,
      spaceBetween: 28,
      speed: prefersReducedMotion ? 0 : 400,
      slidesPerView: 1,
      breakpoints: {
        768: {
          slidesPerView: 2,
          spaceBetween: 32,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 40,
        },
      },
      navigation: {
        prevEl,
        nextEl,
      },
    };

    if (paginationEl) {
      swiperParams.pagination = {
        el: paginationEl,
        clickable: true,
      };
    }

    this.equalizeCardHeights(viewport);

    const swiper = new Swiper(el, swiperParams);

    let resizeRaf = null;
    const onResize = () => {
      if (resizeRaf !== null) {
        cancelAnimationFrame(resizeRaf);
      }
      resizeRaf = requestAnimationFrame(() => {
        this.equalizeCardHeights(viewport);
        swiper.update();
      });
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('load', onResize, { once: true });

    viewport.classList.add('c-slider__viewport--is-swiper');
  }
}
